import { ReactNode, useState } from "react";
import { useDataTableContext } from "../../contexts/DataTableContext";
import useTeamProperties from "../../hooks/useTeamProperties";
import { Accessor, PropertyAttribute } from "../../types/types";
import Preloader from "../UI/Preloader/Preloader";
import Modal, { ModalProps } from "./Modal";
import { Checkbox, Dropdown, Form, Grid, Icon, Loader, Table, TextArea } from "semantic-ui-react";
import EpcRatingTile from "../UI/EpcRatingTile/EpcRatingTile";

import "./GroupPropertiesModal.scss";
import useTeamGroups from "../../hooks/useTeamGroups";
import Button from "../UI/Button/Button";
import { useRestClientContext } from "../../contexts/RestClientContext";
import { useTeamContext } from "../../contexts/TeamContext";
import { useUserContext } from "../../contexts/UserContext";

export interface GroupModalProps extends ModalProps {
	onGroupCreated?: () => void;
}

function sum(values: number[]): number {
	return values.reduce((acc, cur) => acc + cur, 0);
}

function avg(values: number[]): number {
	return sum(values) / values.length;
}

export default function GroupPropertiesModal({ onClose, onGroupCreated }: GroupModalProps) {
	const client = useRestClientContext();
	const team = useTeamContext();
	const user = useUserContext();
	const { selectedIds } = useDataTableContext()!;
	const [selectedGroupId, setSelectedGroupId] = useState<number>(-1);
	const [newGroupName, setNewGroupName] = useState<string>();
	const [newGroupDescription, setNewGroupDescription] = useState<string>("");
	const [newGroupHidden, setNewGroupHidden] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const isAddingToExistingGroup = selectedGroupId !== undefined && selectedGroupId > -1;
	const isCreatingNewGroup = selectedGroupId === -1;
	const valid = isAddingToExistingGroup || newGroupName?.length;

	const accessors: Accessor[] = [
		"location.addr_no",
		"location.addr_street",
		"location.addr_city",
		"location.postcode",
		"heating.main_fuel_co2",
		"metrics.epc_rating",
		"metrics.epc_rating_grade",
	];

	const propertyAttributes: PropertyAttribute[] = ["uprn"];

	const { loading: isLoadingProperties, properties: paginatedAddingProperties } = useTeamProperties({
		pagination: false,
		selectedAccessors: accessors,
		selectedPropertyAttributes: propertyAttributes,
		ids: selectedIds,
	});
	const { loading: isLoadingExistingGroupProperties, properties: paginatedExistingProperties } = useTeamProperties({
		pagination: false,
		selectedAccessors: accessors,
		selectedPropertyAttributes: propertyAttributes,
		groupId: selectedGroupId,
	});
	const { loading: isLoadingGroups, groups = [] } = useTeamGroups();

	const properties = [...(paginatedAddingProperties?.entries ?? [])];

	if (isAddingToExistingGroup) properties.push(...(paginatedExistingProperties?.entries ?? []));

	const loading = isLoadingProperties || isLoadingGroups;

	const onNewGroup = (name: string) => {
		setNewGroupName(name);
		setSelectedGroupId(-1);
	};

	const onChangeGroup = (id: number | "") => {
		if (id === "") setSelectedGroupId(-1);
		else setSelectedGroupId(id);
	};

	const onSubmit = () => {
		setSubmitting(true);

		let promise: Promise<Response>;

		if (isCreatingNewGroup)
			promise = client.createGroup(team.id, {
				name: newGroupName,
				description: newGroupDescription,
				properties: selectedIds,
				is_hidden: newGroupHidden,
			});
		else
			promise = client.updateGroupProperties(
				team.id,
				selectedGroupId,
				properties.map((property) => property.id)
			);

		promise.then(() => {
			setSubmitting(false);
			if (isCreatingNewGroup) onGroupCreated?.();
			// @ts-ignore
			onClose();
		});
	};

	let inner: ReactNode;

	if (loading) inner = <Preloader />;
	else {
		const averageSapValue = avg(properties.map((obj) => Number(obj["metrics.epc_rating"])));

		const groupDropdownOptions = groups.map((group) => ({
			value: group.id,
			text: group.name,
		}));

		if (newGroupName)
			groupDropdownOptions.unshift({
				value: -1,
				text: newGroupName,
			});

		let table: ReactNode;

		if (!isAddingToExistingGroup || !isLoadingExistingGroupProperties)
			table = (
				<Table>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell></Table.HeaderCell>
							<Table.HeaderCell>UPRN</Table.HeaderCell>
							<Table.HeaderCell>Address</Table.HeaderCell>
							<Table.HeaderCell>EPC Rating</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{properties!.map((property) => {
							const isExistingProperty =
								isAddingToExistingGroup &&
								paginatedExistingProperties?.entries.find((iter) => iter.id === property.id);
							return (
								<Table.Row key={property.id}>
									<Table.Cell>
										{!isExistingProperty && <Icon name="add circle" className="adding-to-group" />}
									</Table.Cell>
									<Table.Cell>{property.uprn}</Table.Cell>
									<Table.Cell>
										{property["location.addr_no"]}&nbsp;
										{property["location.addr_street"]},&nbsp;
										{property["location.addr_city"]},&nbsp;
										{property["location.postcode"]}
									</Table.Cell>
									<Table.Cell>
										<EpcRatingTile rating={Number(property["metrics.epc_rating"])} />
									</Table.Cell>
								</Table.Row>
							);
						})}
					</Table.Body>
				</Table>
			);
		else table = <Loader inline active size="mini" className="filters" />;

		inner = (
			<Form>
				<Form.Field>
					<label>Group</label>
					<Dropdown
						placeholder="Choose a group or enter a new group name"
						search
						selection
						fluid
						allowAdditions
						clearable
						options={groupDropdownOptions}
						value={selectedGroupId}
						onAddItem={(e, { value }) => onNewGroup(value as string)}
						onChange={(e, { value }) => onChangeGroup(value as number | "")}
					/>
				</Form.Field>
				{isCreatingNewGroup && (
					<Form.Field>
						<label>Description</label>
						<TextArea
							value={newGroupDescription}
							onChange={(e, { value }) => setNewGroupDescription(value as string)}
						/>
					</Form.Field>
				)}
				{isCreatingNewGroup && user.is_admin && (
					<Form.Field className="group-properties-hide-group">
						<Checkbox
							toggle
							label="Hide group"
							checked={newGroupHidden}
							onChange={() => setNewGroupHidden((prev) => !prev)}
						/>
					</Form.Field>
				)}
				{table}
				<Grid columns={2} verticalAlign="middle">
					<Grid.Row>
						<Grid.Column>Total group properties: {properties.length}</Grid.Column>
					</Grid.Row>
					<Grid.Row>
						<Grid.Column className="average-sap-value">
							Average SAP value:&nbsp;
							<EpcRatingTile rating={averageSapValue} />
							&nbsp;({Math.round(averageSapValue)})
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Form>
		);
	}

	return (
		<Modal open title="Group Properties" onClose={onClose} className="group-properties">
			{inner}
			<Grid textAlign="right">
				<Button variant="outlined" disabled={submitting} onClick={onClose}>
					Cancel
				</Button>
				<Button variant="contained" disabled={loading || !valid || submitting} onClick={onSubmit}>
					Add to Group
				</Button>
			</Grid>
		</Modal>
	);
}
